'use client';

import {
  ChangeEvent,
  FC,
  FormEvent,
  useEffect,
  useContext,
  useMemo,
  useState,
} from 'react';
import { FirmCustomStatusUpdateDialogProps } from './FirmCustomStatusUpdateDialog.types';
import Link from 'next/link';
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Icon,
  Input,
  QuestionnaireCard,
  Separator,
  Text,
  Textarea,
  Toggle,
} from '@/_components';
import {
  FIRM_STATUS_CHANGE_EMAIL_MESSAGE,
  FIRM_STATUS_CHANGE_EMAIL_MESSAGE_FOOTER,
  FIRM_STATUS_CHANGE_SMS_MESSAGE,
  FIRM_STATUS_CHANGE_EMAIL_SUBJECT,
} from '@/_constants/firm';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { FirmCustomStatus } from '@/_types/models/firm';
import { ToastContext } from '@/_contexts/ToastProvider';
import { sendCustomStatusChange } from '@/_api/admin-questionnaires';
import { mapQuestionnaireSection } from '@/_utils/sections';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { UserContext } from '@/_contexts/UserProvider';
import type { Questionnaire } from '@/_types/models/questionnaire';

export const FirmCustomStatusUpdateDialog: FC<
  FirmCustomStatusUpdateDialogProps
> = ({ questionnaireIds, firmStatusId, isOpen, onClose }) => {
  const { addToast } = useContext(ToastContext);
  const { firmCustomStatuses } = useContext(FirmSettingsContext);
  const { questionnaires } = useContext(QuestionnaireContext);
  const { token } = useContext(UserContext);

  const questionnaire: Questionnaire | undefined = questionnaires.find(
    (q) => q.id === questionnaireIds?.[0],
  );
  const questionnaireTitle: string =
    questionnaire?.formatted_name || 'Questionnaire';

  const selectedFirmStatus: FirmCustomStatus | undefined = useMemo(
    () =>
      typeof firmStatusId === 'number'
        ? firmCustomStatuses?.find((status) => status.id === firmStatusId)
        : undefined,
    [firmCustomStatuses, firmStatusId],
  );

  const [isLoading, setLoading] = useState<boolean>(false);
  const [sendText, setSendText] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [text, setText] = useState<string>('');

  useEffect(() => {
    const setFields = () => {
      setSendText(false);
      setLoading(false);

      setSubject(
        `${FIRM_STATUS_CHANGE_EMAIL_SUBJECT} ${selectedFirmStatus?.client_display_status}`,
      );
      setBody(`${FIRM_STATUS_CHANGE_EMAIL_MESSAGE}

<h2>${selectedFirmStatus?.client_display_status}</h2>
<i>${selectedFirmStatus?.client_instruction}</i>

${FIRM_STATUS_CHANGE_EMAIL_MESSAGE_FOOTER}`);
      setText(
        `${FIRM_STATUS_CHANGE_SMS_MESSAGE} ${selectedFirmStatus?.client_display_status}`,
      );
    };

    setFields();

    return () => setFields();
  }, [
    questionnaireIds,
    selectedFirmStatus?.client_display_status,
    selectedFirmStatus?.client_instruction,
  ]);

  const onToggleText = () => setSendText((previous) => !previous);
  const onSubjectChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSubject(event.target.value);
  const onBodyChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setBody(event.target.value);
  const onTextChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setText(event.target.value);

  const onNotificationSend = async (event: FormEvent) => {
    event.preventDefault();

    if (!areFormValuesValid) return;

    try {
      setLoading(true);

      await sendCustomStatusChange({
        questionnaireIds: questionnaireIds!,
        customEmailSubject: subject,
        customEmailMessage: body,
        customSmsMessage: sendText && text ? text : undefined,
        token,
      });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const mappedSections =
    questionnaire?.sections?.map((s) => mapQuestionnaireSection(s)) || [];

  const areFormValuesValid: boolean = useMemo(
    () => (!!subject && !!body && sendText ? !!text : true),
    [subject, body, sendText, text],
  );

  const hasError: boolean = useMemo(
    () =>
      isOpen &&
      (!selectedFirmStatus?.client_display_status ||
        !selectedFirmStatus?.client_instruction),
    [
      isOpen,
      selectedFirmStatus?.client_display_status,
      selectedFirmStatus?.client_instruction,
    ],
  );

  // To not waste resources rendering Dialogs for unopened questionnaires
  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      disablePadding={true}
      onClose={onClose}
      title={`Notify Status Change to ${selectedFirmStatus?.client_display_status}`}
    >
      <Dialog.Content>
        {hasError && (
          <Alert type="danger">
            Could not load custom status information. Please make sure the email
            template is filled out correctly.
          </Alert>
        )}
        <Flex isColumn={true} gap={6} padding={12} backgroundColor="subtle">
          <Flex isYCentered={true} gap={2}>
            <Icon size={16} color="secondary" name="info" />
            <Text type="small" color="secondary">
              Preview of Client-Facing Status &amp; Instruction
            </Text>
          </Flex>
          <QuestionnaireCard
            customStatus={selectedFirmStatus?.client_display_status}
            title={questionnaireTitle}
            isPreview={true}
            sections={mappedSections}
            status="ready_to_start"
            description={selectedFirmStatus?.client_instruction}
          />
        </Flex>
        <Separator />
        <Flex isColumn={true} gap={12} padding={12}>
          <Flex isColumn={true} gap={6}>
            <Input
              label="Subject"
              placeholder="Subject"
              isDisabled={isLoading}
              value={subject}
              onChange={onSubjectChange}
            />
            <Textarea
              rows={5}
              label="Body"
              placeholder="Message"
              isDisabled={isLoading}
              value={body}
              onChange={onBodyChange}
            />
            <Flex isColumn={true} gap={6}>
              <Toggle
                isChecked={sendText || isLoading}
                label="Send SMS message?"
                onChange={onToggleText}
              />
              <Textarea
                rows={5}
                isDisabled={!sendText || isLoading}
                label="SMS notification"
                placeholder="SMS message"
                value={text}
                helperText="The message will be sent if the client has a phone number and has
              opted in to receive SMS notification. The message will also contain a
              link to the Questionnaire."
                onChange={onTextChange}
              />
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <Flex isBetween={true} gap={8} isYCentered={true}>
          <Text type="small">
            You can edit <em>Custom Statuses</em> in{' '}
            <Link href="/firm">Firm Settings</Link>.
          </Text>
          <ButtonGroup align="right" isSameLine={true}>
            <Button
              intent="secondary"
              value="Don't Send"
              isDisabled={isLoading}
              onClick={onClose}
            />
            <Button
              intent="primary"
              isLoading={isLoading}
              isDisabled={isLoading || !areFormValuesValid}
              value="Send message"
              onClick={onNotificationSend}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
};
